<template>
  <section class="provider-profile-shell">
    <div class="provider-profile-intro">
      <div>
        <label class="active teal-text">Provider Profile</label>
        <h6>Account and payment details</h6>
        <p>
          Keep the provider's contact, medical director, and settlement details accurate for operational use.
        </p>
      </div>
      <span class="provider-profile-count">7 records</span>
    </div>

    <div class="provider-profile-sync">
      <div>
        <label class="active teal-text">oneProvider Sync</label>
        <h6>{{ status ? "Provider is synced" : "Provider is pending sync" }}</h6>
        <p>
          Synced providers keep their provider name locked here so the shared provider identity stays consistent.
        </p>
      </div>
      <span class="provider-profile-status" :class="{ 'is-active': status }">
        {{ status ? "Active" : "Pending" }}
      </span>
    </div>

    <div class="provider-profile-grid">
      <div
        class="provider-profile-field is-wide"
        :class="{
          'is-editing': editId == 'name',
          'is-locked': status,
        }"
      >
        <div class="provider-profile-field-main">
          <label class="active teal-text" for="name">Provider Name</label>
          <h5 v-if="editId != 'name'">{{ name || "Not recorded" }}</h5>
          <input id="name" type="text" v-model="name" v-else />
          <small v-if="status">Locked because this provider is synced with oneProvider.</small>
        </div>
        <button
          type="button"
          class="provider-profile-icon is-locked"
          disabled
          v-if="status"
          title="Locked"
        >
          <i class="material-icons">lock</i>
        </button>
        <button
          type="button"
          class="provider-profile-icon"
          @click.prevent="handleEdit('name')"
          title="Edit provider name"
          v-if="!status && editId != 'name'"
        >
          <i class="material-icons">edit</i>
        </button>
        <div class="provider-profile-actions" v-if="editId == 'name'">
          <button
            type="button"
            class="btn-flat provider-profile-cancel"
            :class="{ disabled: isSubmitting }"
            @click.prevent="handleBack"
          >
            Cancel
          </button>
          <button
            type="button"
            class="btn provider-profile-submit"
            :class="{ disabled: isSubmitting }"
            @click.prevent="handleSubmit('name', name)"
          >
            Update
          </button>
        </div>
      </div>

      <div class="provider-profile-field" :class="{ 'is-editing': editId == 'md' }">
        <div class="provider-profile-field-main">
          <label class="active teal-text" for="md">Medical Director</label>
          <h6 v-if="editId != 'md'">{{ md || "Not recorded" }}</h6>
          <input id="md" type="text" v-model="md" v-else />
        </div>
        <button
          type="button"
          class="provider-profile-icon"
          @click.prevent="handleEdit('md')"
          title="Edit medical director"
          v-if="editId != 'md'"
        >
          <i class="material-icons">edit</i>
        </button>
        <div class="provider-profile-actions" v-if="editId == 'md'">
          <button
            type="button"
            class="btn-flat provider-profile-cancel"
            :class="{ disabled: isSubmitting }"
            @click.prevent="handleBack"
          >
            Cancel
          </button>
          <button
            type="button"
            class="btn provider-profile-submit"
            :class="{ disabled: isSubmitting }"
            @click.prevent="handleSubmit('md', md)"
          >
            Update
          </button>
        </div>
      </div>

      <div class="provider-profile-field" :class="{ 'is-editing': editId == 'phone' }">
        <div class="provider-profile-field-main">
          <label class="active teal-text" for="phone">Provider Phone</label>
          <h6 v-if="editId != 'phone'">{{ phone || "Not recorded" }}</h6>
          <input id="phone" type="text" v-model="phone" v-else />
        </div>
        <button
          type="button"
          class="provider-profile-icon"
          @click.prevent="handleEdit('phone')"
          title="Edit provider phone"
          v-if="editId != 'phone'"
        >
          <i class="material-icons">edit</i>
        </button>
        <div class="provider-profile-actions" v-if="editId == 'phone'">
          <button
            type="button"
            class="btn-flat provider-profile-cancel"
            :class="{ disabled: isSubmitting }"
            @click.prevent="handleBack"
          >
            Cancel
          </button>
          <button
            type="button"
            class="btn provider-profile-submit"
            :class="{ disabled: isSubmitting }"
            @click.prevent="handleSubmit('phone', phone)"
          >
            Update
          </button>
        </div>
      </div>

      <div
        class="provider-profile-field"
        :class="{
          'is-editing': editId == 'email',
          'is-locked': !allowEmailChange,
        }"
      >
        <div class="provider-profile-field-main">
          <label class="active teal-text" for="email">Provider Email</label>
          <h6 v-if="editId != 'email'">{{ email || "Not recorded" }}</h6>
          <div class="provider-profile-email-input-wrap" v-else>
            <input
              id="email"
              name="provider-profile-email-edit"
              type="email"
              autocomplete="new-password"
              autocapitalize="none"
              autocorrect="off"
              spellcheck="false"
              v-model="email"
            />
            <div class="progress provider-profile-inline-progress" v-if="isCheckingEmail">
              <div class="indeterminate"></div>
            </div>
          </div>
          <small v-if="!allowEmailChange">{{ emailLockReason }}</small>
          <small class="provider-profile-checking" v-else-if="isCheckingEmail">
            Checking email availability...
          </small>
          <small class="provider-profile-error" v-else-if="emailDuplicateCounter > 0">
            Sorry, email already belongs to another provider.
          </small>
        </div>
        <button
          type="button"
          class="provider-profile-icon is-locked"
          disabled
          v-if="!allowEmailChange"
          title="Locked"
        >
          <i class="material-icons">lock</i>
        </button>
        <button
          type="button"
          class="provider-profile-icon"
          @click.prevent="handleEdit('email')"
          title="Edit provider email"
          v-if="allowEmailChange && editId != 'email'"
        >
          <i class="material-icons">edit</i>
        </button>
        <div class="provider-profile-actions" v-if="editId == 'email'">
          <button
            type="button"
            class="btn-flat provider-profile-cancel"
            :class="{ disabled: isSubmitting }"
            @click.prevent="handleBack"
          >
            Cancel
          </button>
          <button
            type="button"
            class="btn provider-profile-submit"
            :class="{ disabled: isSubmitting || isCheckingEmail || emailDuplicateCounter > 0 }"
            @click.prevent="handleSubmit('email', email)"
          >
            Update
          </button>
        </div>
      </div>

      <div class="provider-profile-field" :class="{ 'is-editing': editId == 'address' }">
        <div class="provider-profile-field-main">
          <label class="active teal-text" for="address">Provider Address</label>
          <h6 v-if="editId != 'address'">{{ address || "Not recorded" }}</h6>
          <input id="address" type="text" v-model="address" v-else />
        </div>
        <button
          type="button"
          class="provider-profile-icon"
          @click.prevent="handleEdit('address')"
          title="Edit provider address"
          v-if="editId != 'address'"
        >
          <i class="material-icons">edit</i>
        </button>
        <div class="provider-profile-actions" v-if="editId == 'address'">
          <button
            type="button"
            class="btn-flat provider-profile-cancel"
            :class="{ disabled: isSubmitting }"
            @click.prevent="handleBack"
          >
            Cancel
          </button>
          <button
            type="button"
            class="btn provider-profile-submit"
            :class="{ disabled: isSubmitting }"
            @click.prevent="handleSubmit('address', address)"
          >
            Update
          </button>
        </div>
      </div>

      <div class="provider-profile-field" :class="{ 'is-editing': editId == 'bank' }">
        <div class="provider-profile-field-main">
          <label class="active teal-text" for="bank">Bank Name</label>
          <h6 v-if="editId != 'bank'">{{ bank || "Not recorded" }}</h6>
          <input id="bank" type="text" v-model="bank" v-else />
        </div>
        <button
          type="button"
          class="provider-profile-icon"
          @click.prevent="handleEdit('bank')"
          title="Edit bank name"
          v-if="editId != 'bank'"
        >
          <i class="material-icons">edit</i>
        </button>
        <div class="provider-profile-actions" v-if="editId == 'bank'">
          <button
            type="button"
            class="btn-flat provider-profile-cancel"
            :class="{ disabled: isSubmitting }"
            @click.prevent="handleBack"
          >
            Cancel
          </button>
          <button
            type="button"
            class="btn provider-profile-submit"
            :class="{ disabled: isSubmitting }"
            @click.prevent="handleSubmit('bank', bank)"
          >
            Update
          </button>
        </div>
      </div>

      <div class="provider-profile-field" :class="{ 'is-editing': editId == 'account' }">
        <div class="provider-profile-field-main">
          <label class="active teal-text" for="account">Bank Account</label>
          <h6 v-if="editId != 'account'">{{ account || "Not recorded" }}</h6>
          <input id="account" type="text" v-model="account" v-else />
        </div>
        <button
          type="button"
          class="provider-profile-icon"
          @click.prevent="handleEdit('account')"
          title="Edit bank account"
          v-if="editId != 'account'"
        >
          <i class="material-icons">edit</i>
        </button>
        <div class="provider-profile-actions" v-if="editId == 'account'">
          <button
            type="button"
            class="btn-flat provider-profile-cancel"
            :class="{ disabled: isSubmitting }"
            @click.prevent="handleBack"
          >
            Cancel
          </button>
          <button
            type="button"
            class="btn provider-profile-submit"
            :class="{ disabled: isSubmitting }"
            @click.prevent="handleSubmit('account', account)"
          >
            Update
          </button>
        </div>
      </div>
    </div>
  </section>
</template>
  
<script>
import { computed, ref, onMounted, watchEffect } from "vue";
import getItems from "@/composables/getItems";
import putItem from "@/composables/putItem";
import {
  debounce,
  getCachedPayload,
  hasBasicEmailShape,
  setCachedPayload,
} from "@/composables/requestOptimization";

export default {
  name: "ProfileClientComponent",
  props: ["provider"],
  emits: ["update"],
  setup(props, context) {
    const status = ref(null);
    const name = ref(null);
    const address = ref(null);
    const email = ref(null);
    const allowEmailChange = ref(false);
    const emailLockReason = ref(null);
    const emailDuplicateCounter = ref(0);
    const isCheckingEmail = ref(false);
    const isSubmitting = ref(false);
    const error = ref(null);
    const phone = ref(null);
    const md = ref(null);
    const bank = ref(null);
    const account = ref(null);
    const editId = ref(null);
    const response = ref(props.provider);
    const emailCheckCacheTtl = 60 * 1000;
    let emailCheckRequestId = 0;

    const normalizedEmail = computed(() => {
      return email.value ? email.value.trim().toLowerCase() : null;
    });

    // set all
    const handleLoad = () => {
      const hasProviderUser = response.value?.user_created === "y";
      const isSyncedProvider = response.value?.opid != null;

      status.value = isSyncedProvider;
      name.value = response.value.name;
      address.value = response.value.address;
      email.value = response.value.email;
      emailDuplicateCounter.value = 0;
      isCheckingEmail.value = false;
      allowEmailChange.value = !(hasProviderUser || isSyncedProvider);
      emailLockReason.value = hasProviderUser
        ? "Locked because a portal user has already been created for this provider."
        : isSyncedProvider
          ? "Locked because this provider is synced with oneProvider."
          : null;
      phone.value = response.value.phone;
      md.value = response.value.md;
      bank.value = response.value.bank;
      account.value = response.value.account;
    };

    const runEmailAvailabilityCheck = debounce(async (currentEmail, requestId) => {
      if (requestId !== emailCheckRequestId || currentEmail !== normalizedEmail.value) {
        return;
      }

      const cacheKey = `provider-email-check:${currentEmail}:${props.provider.id}`;
      const cachedCount = getCachedPayload(cacheKey);

      if (cachedCount !== null) {
        emailDuplicateCounter.value = cachedCount;
        isCheckingEmail.value = false;
        return;
      }

      const { res } = await getItems(`provider/email/checker?email=${encodeURIComponent(currentEmail)}&provider_id=${props.provider.id}`);

      if (requestId !== emailCheckRequestId || currentEmail !== normalizedEmail.value) {
        return;
      }

      if (res.value) {
        emailDuplicateCounter.value = res.value.data;
        setCachedPayload(cacheKey, res.value.data, emailCheckCacheTtl);
      } else {
        emailDuplicateCounter.value = 1;
      }

      isCheckingEmail.value = false;
    });

    // update all
    const handleUpdate = () => {
      response.value.name = name.value;
      response.value.address = address.value;
      response.value.email = email.value
      response.value.phone = phone.value;
      response.value.md = md.value;
      response.value.bank = bank.value;
      response.value.account = account.value;
    };

    // initialize
    onMounted(() => {
      handleLoad();
    });

    // handle Edit
    const handleEdit = (val) => {
      editId.value = val;
    };

    // handle Back
    const handleBack = () => {
      editId.value = null;
      // go back to saved state
      handleLoad();
    };

    // handle Submit
    const handleSubmit = async (key, val) => {
      if (key === "email" && (isCheckingEmail.value || emailDuplicateCounter.value > 0)) {
        return;
      }

      // start
      isSubmitting.value = true;
      error.value = false;

      if (val) {
        // prepare for take off
        const newVal = {
          [key]: val,
        };

        // post item
        const { res, err } = await putItem(
          "provider",
          props.provider.id,
          newVal
        );

        if (res.value) {
          // next
          //update all
          handleUpdate();

          // update record with parent
          context.emit("update", response.value);
        } else {
          error.value = err.value;
        }
      }

      // end here
      isSubmitting.value = false;

      // close
      handleBack();
    };

    watchEffect(() => {
      if (editId.value !== "email" || !allowEmailChange.value) {
        return;
      }

      const currentEmail = normalizedEmail.value;
      const savedEmail = response.value?.email
        ? response.value.email.trim().toLowerCase()
        : null;

      if (!currentEmail || currentEmail === savedEmail) {
        emailDuplicateCounter.value = 0;
        isCheckingEmail.value = false;
        emailCheckRequestId++;
        return;
      }

      if (!hasBasicEmailShape(currentEmail)) {
        emailDuplicateCounter.value = 1;
        isCheckingEmail.value = false;
        emailCheckRequestId++;
        return;
      }

      // search email
      isCheckingEmail.value = true;
      emailDuplicateCounter.value = 0;
      emailCheckRequestId++;
      runEmailAvailabilityCheck(currentEmail, emailCheckRequestId);
    });

    return {
      handleEdit,
      handleBack,
      handleSubmit,
      isSubmitting,
      error,
      editId,
      status,
      name,
      address,
      email,
      allowEmailChange,
      emailLockReason,
      emailDuplicateCounter,
      isCheckingEmail,
      phone,
      md,
      bank,
      account,
    };
  },
};
</script>
  
  
<style scoped>
.provider-profile-shell {
  color: #263238;
  display: grid;
  gap: 14px;
}

.provider-profile-intro,
.provider-profile-sync,
.provider-profile-field {
  background: #ffffff;
  border: 1px solid #c4d3d9;
  border-radius: 6px;
  box-shadow: 0 14px 24px -28px rgba(38, 50, 56, 0.72);
}

.provider-profile-intro,
.provider-profile-sync {
  align-items: center;
  display: flex;
  gap: 18px;
  justify-content: space-between;
  padding: 18px 20px;
}

.provider-profile-intro h6,
.provider-profile-sync h6,
.provider-profile-field h5,
.provider-profile-field h6 {
  color: #263238;
  margin: 5px 0 0;
  overflow-wrap: anywhere;
}

.provider-profile-intro label,
.provider-profile-sync label,
.provider-profile-field label {
  display: block;
  font-size: 0.82rem;
  font-weight: 600;
  margin-bottom: 8px;
}

.provider-profile-intro h6,
.provider-profile-sync h6 {
  font-size: 1.08rem;
  font-weight: 600;
  line-height: 1.35;
}

.provider-profile-field h5 {
  font-size: 1.18rem;
  font-weight: 600;
  line-height: 1.32;
}

.provider-profile-field h6 {
  font-size: 0.96rem;
  font-weight: 600;
  line-height: 1.38;
}

.provider-profile-intro p,
.provider-profile-sync p,
.provider-profile-field small {
  color: #607d8b;
  display: block;
  font-size: 0.94rem;
  line-height: 1.55;
  margin: 0;
}

.provider-profile-field small {
  font-size: 0.86rem;
  margin-top: 6px;
}

.provider-profile-field-main {
  position: relative;
}

.provider-profile-field-main input:-webkit-autofill,
.provider-profile-field-main input:-webkit-autofill:hover,
.provider-profile-field-main input:-webkit-autofill:focus {
  -webkit-text-fill-color: #263238;
  box-shadow: 0 0 0 1000px #fffdf5 inset;
  transition: background-color 9999s ease-out 0s;
}

.provider-profile-error {
  color: #b71c1c !important;
  font-weight: 700;
}

.provider-profile-checking {
  color: #78909c !important;
  font-weight: 600;
}

.provider-profile-email-input-wrap {
  position: relative;
}

.provider-profile-email-input-wrap input {
  display: block;
}

.provider-profile-email-input-wrap .provider-profile-inline-progress {
  background-color: #e8f3f2;
  border-radius: 0;
  bottom: 0;
  height: 3px;
  left: 0;
  margin: 0;
  position: absolute;
  right: 0;
  transform: translateY(2px);
  width: 100%;
  z-index: 2;
}

.provider-profile-email-input-wrap .provider-profile-inline-progress .indeterminate {
  background-color: #00897b;
}

.provider-profile-count,
.provider-profile-status {
  background: #f8fafb;
  border: 1px solid #dfe8eb;
  border-radius: 999px;
  color: #455a64;
  flex: 0 0 auto;
  font-size: 0.78rem;
  font-weight: 700;
  padding: 8px 12px;
}

.provider-profile-status.is-active {
  background: #e8f3f2;
  border-color: #9bd2cb;
  color: #00695c;
}

.provider-profile-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.provider-profile-field {
  align-items: center;
  display: grid;
  gap: 14px;
  grid-template-columns: minmax(0, 1fr) auto;
  min-height: 112px;
  padding: 18px;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.provider-profile-field:hover,
.provider-profile-field:focus-within {
  border-color: #80cbc4;
  box-shadow: 0 16px 26px -26px rgba(0, 105, 92, 0.62);
  transform: translateY(-1px);
}

.provider-profile-field.is-wide {
  grid-column: 1 / -1;
  min-height: 108px;
}

.provider-profile-field.is-editing {
  background: #fffdf5;
  border-color: #ffd54f;
  box-shadow: 0 16px 28px -28px rgba(121, 85, 72, 0.72);
}

.provider-profile-field.is-locked {
  background: #f8fafb;
  border-color: #d5e1e5;
}

.provider-profile-field-main input {
  border-bottom-color: #b0bec5;
  color: #263238;
  font-size: 1rem;
  font-weight: 700;
  margin: 8px 0 0;
}

.provider-profile-icon {
  align-items: center;
  background: #ffffff;
  border: 1px solid #c4d3d9;
  border-radius: 6px;
  color: #00897b;
  cursor: pointer;
  display: inline-flex;
  height: 38px;
  justify-content: center;
  padding: 0;
  transition: background-color 0.18s ease, border-color 0.18s ease, color 0.18s ease;
  width: 38px;
}

.provider-profile-icon:hover {
  background: #e0f2f1;
  border-color: #80cbc4;
  color: #00695c;
}

.provider-profile-icon i {
  font-size: 19px;
}

.provider-profile-icon.is-locked {
  background: #eceff1;
  border-color: #d8e0e3;
  color: #78909c;
  cursor: default;
}

.provider-profile-actions {
  align-items: center;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.provider-profile-cancel {
  color: #455a64;
  font-weight: 700;
  text-transform: none;
}

.provider-profile-submit {
  background: #00897b;
  border-radius: 6px;
  box-shadow: none;
  font-weight: 700;
  text-transform: none;
}

@media only screen and (max-width: 900px) {
  .provider-profile-grid {
    grid-template-columns: 1fr;
  }
}

@media only screen and (max-width: 600px) {
  .provider-profile-intro,
  .provider-profile-sync,
  .provider-profile-field {
    align-items: flex-start;
    grid-template-columns: 1fr;
  }

  .provider-profile-intro,
  .provider-profile-sync {
    flex-direction: column;
  }

  .provider-profile-actions {
    justify-content: flex-start;
  }
}
</style>
